import React, {
  useEffect,
  useState,
  ReactNode,
  RefObject,
  MouseEvent,
  useRef,
} from 'react';
import defer from 'lodash/defer';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import clsx from 'clsx';
import EditIcon from 'core/icons/edit';
import ToolTip from 'core/components/Tooltip';

type EditableChildrenFn = (
  onSubmit: () => void,
  onCancel: () => void,
  ref?: RefObject<HTMLElement>,
) => ReactNode;

export type EditableInstance = {
  setIsEdit: (value: boolean) => void;
  triggerSubmit: () => void;
  triggerCancel: () => void;
};

type Props = {
  instanceRef?: RefObject<EditableInstance>;
  children: ReactNode | EditableChildrenFn;
  idleView: ReactNode;
  readonly?: boolean;
  isLoading?: boolean;
  controlRef?: RefObject<HTMLElement>;
  onShowControl?: (e: MouseEvent<HTMLElement>) => void;
  onSubmit?: () => boolean | Promise<void>;
  onCancel?: () => void;
  classes?: Partial<Record<
    'idleRoot'
    | 'editableRoot'
    | 'iconRoot'
    | 'icon', string>>;
  showActions?: boolean;
  tooltip?: string;
  showEdit?: boolean;
};

export default function Editable({
  readonly = false,
  instanceRef,
  classes,
  children,
  idleView = null,
  isLoading,
  controlRef,
  onShowControl = noop,
  onSubmit = () => true,
  onCancel = noop,
  tooltip,
  showEdit = false,
}: Props) {
  const hasIdleView = !isNil(idleView);
  const idleButtonRef = useRef<HTMLDivElement>(null);
  const [isEdit, setIsEdit] = useState<boolean>(showEdit || !hasIdleView);
  useEffect(() => {
    if (isEdit && hasIdleView) {
      defer(() => {
        if (controlRef?.current instanceof HTMLInputElement) {
          controlRef?.current?.focus();
        }
      });
    }
  }, [isEdit, hasIdleView]);
  const hasTooltip = !isNil(tooltip);
  const idleClassName = clsx(
    'populate-editable-idle flex items-center rounded p-1 relative space-x-2 w-fit',
    {
      'hover:shadow hover:shadow-gray-200 hover:bg-white border border-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-default-input-focused': !readonly && !isLoading,
    },
    { '!cursor-text': readonly },
    classes?.idleRoot,
  );
  const showControl = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setIsEdit(!readonly);
    onShowControl(e);
  };

  const resetToIdle = () => {
    setIsEdit(false);
    defer(() => {
      idleButtonRef.current?.focus();
    });
  };

  const handleCancel = () => {
    onCancel?.();
    if (hasIdleView) {
      resetToIdle();
    }
  };
  const handleSubmit = () => {
    const submitted = onSubmit?.() ?? true;
    if (submitted && hasIdleView) {
      resetToIdle();
    }
  };

  useEffect(() => {
    if (isEdit && readonly) {
      setIsEdit(false);
    }
  }, [readonly, isEdit]);

  useEffect(() => {
    if (!isNil(instanceRef)) {
      instanceRef.current = {
        setIsEdit,
        triggerSubmit: handleSubmit,
        triggerCancel: handleCancel,
      };
    }
  }, []);

  return isEdit || !hasIdleView
    ? (
      <div className={clsx('populate-editable-active flex flex-col w-full justify-start', classes?.editableRoot)}>
        {typeof children === 'function'
          ? children(handleSubmit, handleCancel, controlRef)
          : children}
      </div>
    ) : (
      <div
        ref={idleButtonRef}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            showControl(e as unknown as MouseEvent<HTMLElement>);
          }
        }}
        className={idleClassName}
        onClick={showControl}
      >
        {isLoading ? 'Updating...' : idleView}
        {(!isLoading && !readonly) && (
          <span className={clsx('populate-editable-trigger ml-1', classes?.iconRoot)}>
            <ToolTip
              title={tooltip}
              hide={!hasTooltip}
            >
              <span>
                <EditIcon className={clsx('!text-xl !text-inherit', classes?.icon)} />
              </span>
            </ToolTip>
          </span>
        )}
      </div>
    );
}
